import { mapGetters } from 'vuex';
import styled from 'vue-styled-components';
import VirtualList from 'vue-virtual-scroll-list';
import dropdownMixmin from '../mixins';

import RingLoader from '@components/Common/RingLoader';

import * as S from './styles';

import CheckIcon from '@svgIcons/check.svg';
import { LINE_CLAMP_DIR } from '@consts';
import { isEllipsisActive } from '@util';

export default {
    name: 'DefaultDropdown',
    mixins: [dropdownMixmin],
    props: {
        selectedOption: String | Number | Object,
        blockedOptions: {
            type: Array,
            default() {
                return [];
            },
        },
        useVirtualScroll: Boolean,
        offSetTop: {
            type: Number,
            default: 0,
        },
        extraComponents: {
            type: Array,
            default() {
                return [];
            },
        },
        lineClampOption: {
            type: Object | undefined,
            default() {
                return R.assocPath(
                    ['directives', 0, 'value'],
                    1,
                    LINE_CLAMP_DIR
                );
            },
        },
        getOption: {
            type: Function,
            default: R.identity,
        },
        className: {
            type: String,
            default: '',
        },
        additionalIconRenderer: Function,
        isLoading: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            currentSelectedOption: '',
            activeTooltipKey: null,
            activeTooltipText: '',
            anchorRect: { top: 0, left: 0, width: 0, height: 0 },
        };
    },
    watch: {
        selectedOption: {
            handler(value) {
                this.currentSelectedOption = value;
            },
            deep: true,
            immediate: true,
        },
    },
    computed: {
        Wrapper() {
            const { customCss } = this;

            return customCss
                ? styled(S.ComponentWrapper)`
                      ${customCss}
                  `
                : S.ComponentWrapper;
        },
        virtualListSources() {
            const { filteredOptions } = this;

            return filteredOptions.map((option, index) => ({
                index,
                key: option + index,
                option: option,
            }));
        },
        selectedIdx() {
            const { filteredOptions, currentSelectedOption, selectedOption } =
                this;

            return filteredOptions.findIndex(
                R.equals(currentSelectedOption || selectedOption)
            );
        },
    },
    methods: {
        updateSelectedItem(option) {
            const { hasConfirmButton, submit } = this;

            if (!hasConfirmButton) {
                submit({ value: option });
            }

            this.currentSelectedOption = option;
        },
        onEllipsisOptionEnter(key, tooltip, ev) {
            const el = ev?.currentTarget;
            const textEl = el?.querySelector?.('p.text');
            if (el && textEl && isEllipsisActive(textEl)) {
                const wrapperEl = this.$refs.optionListWrapper?.$el || this.$refs.optionListWrapper;
                if (!wrapperEl) return;
                const itemRect = el.getBoundingClientRect();
                const wrapperRect = wrapperEl.getBoundingClientRect();
                this.anchorRect = {
                    top: itemRect.top - wrapperRect.top,
                    left: itemRect.left - wrapperRect.left,
                    width: itemRect.width,
                    height: itemRect.height,
                };
                this.activeTooltipText = tooltip;
                // Defer visibility toggle by one frame so the anchor span's
                // inline style is painted before antd mounts and measures it.
                if (this._tooltipShowRaf) cancelAnimationFrame(this._tooltipShowRaf);
                this._tooltipShowRaf = requestAnimationFrame(() => {
                    this.activeTooltipKey = key;
                    this._tooltipShowRaf = null;
                });
            } else if (this.activeTooltipKey !== null) {
                if (this._tooltipShowRaf) {
                    cancelAnimationFrame(this._tooltipShowRaf);
                    this._tooltipShowRaf = null;
                }
                this.activeTooltipKey = null;
            }
        },
        onOptionListScroll() {
            if (this._tooltipShowRaf) {
                cancelAnimationFrame(this._tooltipShowRaf);
                this._tooltipShowRaf = null;
            }
            if (this.activeTooltipKey !== null) this.activeTooltipKey = null;
        },
        renderVirtual() {
            const {
                virtualListSources,
                selectedIdx,
                showSelected,
                wrapTooltip,
                lineClampOption,
                renderOption,
                tooltipGenerator,
                blockedOptions,
                tooltipOnEllipsis,
                onEllipsisOptionEnter,
            } = this;

            return (
                <VirtualList
                    class="dropdown-virtual-scroller"
                    data-key="key"
                    data-sources={virtualListSources}
                    data-component={ItemElement}
                    keeps={20}
                    extra-props={{
                        selectedIdx,
                        showSelected,
                        wrapTooltip,
                        lineClampOption,
                        renderOption,
                        tooltipGenerator,
                        blockedOptions,
                        tooltipOnEllipsis,
                        onEllipsisOptionEnter,
                    }}
                />
            );
        },
        renderOptionList() {
            const {
                width,
                height,
                hasConfirmButton,
                hasCancelButton,
                filteredOptions = [],
                renderEmptyOption,
                currentSelectedOption,
                updateSelectedItem,
                renderOption,
                hasSearchBar,
                showSelected,
                allowEmptyValue,
                wrapTooltip,
                tooltipGenerator,
                renderVirtual,
                useVirtualScroll,
                tooltipOnEllipsis,
                blockedOptions,
                lineClampOption,
                getOption,
                additionalIconRenderer,
                isLoading,
                activeTooltipKey,
                activeTooltipText,
                anchorRect,
            } = this;

            const hasBottomBorder = hasConfirmButton || hasCancelButton;

            const selectedIdx = filteredOptions.findIndex(
                R.equals(currentSelectedOption || this.selectedOption)
            );

            const handleClick = (e) => {
                e.stopPropagation();
                let idx;
                let el$ = e.target;
                const getIdx = R.path(['dataset', 'index']);

                while (idx === undefined && el$) {
                    idx = getIdx(el$);
                    el$ = el$.parentNode;
                }

                const option = filteredOptions[idx];
                const isBlocked =
                    blockedOptions.indexOf(option) > -1 || option?.disabled;

                if (isBlocked) {
                    return;
                } else {
                    if (allowEmptyValue) {
                        updateSelectedItem(option);
                    } else {
                        option && updateSelectedItem(option);
                    }
                }
            };

            if (isLoading) {
                return (
                    <S.LoaderWrapper>
                        <RingLoader />
                    </S.LoaderWrapper>
                );
            }

            return (
                <S.OptionListWrapper
                    ref="optionListWrapper"
                    width={width}
                    height={height}
                    hasBottomBorder={hasBottomBorder}
                    hasSearchBar={hasSearchBar}
                    onClick={handleClick}
                    onMouseleave={() => {
                        if (this._tooltipShowRaf) {
                            cancelAnimationFrame(this._tooltipShowRaf);
                            this._tooltipShowRaf = null;
                        }
                        this.activeTooltipKey = null;
                    }}
                    useVirtualScroll={useVirtualScroll}
                >
                    {filteredOptions.length ? (
                        <ul>
                            {useVirtualScroll
                                ? renderVirtual()
                                : filteredOptions.map((option, index) => {
                                      const isBlocked =
                                          blockedOptions.indexOf(
                                              getOption?.(option) ?? option
                                          ) > -1;

                                      const isSelectedOption =
                                          index === selectedIdx;

                                      const text = renderOption(option);
                                      const optionKey = option + index;
                                      const tooltip = tooltipGenerator(option);

                                      const optionContent = (
                                          <S.OptionItem
                                              isSelectedOption={isSelectedOption}
                                              key={index}
                                              isBlocked={isBlocked}
                                              class={`option-item vivid ${
                                                  isSelectedOption
                                                      ? 'selected-option-item'
                                                      : ''
                                              }`}
                                              data-index={index}
                                              tooltipOnEllipsis={tooltipOnEllipsis}
                                              {...(tooltipOnEllipsis && {
                                                  onMouseenter: (e) => this.onEllipsisOptionEnter(optionKey, tooltip, e),
                                              })}
                                          >
                                              <p
                                                  class="text"
                                                  {...(!tooltipOnEllipsis &&
                                                      lineClampOption)}
                                              >
                                                  {text}
                                              </p>
                                              <S.CheckItemWrapper>
                                                  {showSelected &&
                                                      isSelectedOption && (
                                                          <CheckIcon
                                                              v-view-box
                                                              class="check-icon"
                                                          />
                                                      )}
                                                  {additionalIconRenderer &&
                                                      additionalIconRenderer(
                                                          option
                                                      )}
                                              </S.CheckItemWrapper>
                                          </S.OptionItem>
                                      );

                                      if (tooltipOnEllipsis) {
                                          return optionContent;
                                      }
                                      return wrapTooltip({
                                          visible: Boolean(tooltip),
                                          title: tooltip,
                                          content: optionContent,
                                      });
                                  })}
                        </ul>
                    ) : (
                        renderEmptyOption()
                    )}
                    {tooltipOnEllipsis && (
                        <a-tooltip
                            key={activeTooltipKey || 'tooltip-hidden'}
                            visible={activeTooltipKey !== null}
                            title={activeTooltipText}
                            getPopupContainer={() => document.body}
                            placement="top"
                            destroyTooltipOnHide={true}
                            // popup sits outside the wrapper: overlapping the cursor would
                            // fire the wrapper mouseleave-clear and loop show/hide
                            overlayStyle={{ pointerEvents: 'none' }}
                        >
                            <span
                                ref="tooltipAnchor"
                                style={{
                                    position: 'absolute',
                                    pointerEvents: 'none',
                                    opacity: 0,
                                    top: `${anchorRect.top}px`,
                                    left: `${anchorRect.left}px`,
                                    width: `${anchorRect.width}px`,
                                    height: `${anchorRect.height}px`,
                                }}
                            />
                        </a-tooltip>
                    )}
                </S.OptionListWrapper>
            );
        },
    },
    mounted() {
        this.$el.addEventListener('scroll', this.onOptionListScroll, true);
    },
    beforeDestroy() {
        this.$el.removeEventListener('scroll', this.onOptionListScroll, true);
        if (this._tooltipShowRaf) cancelAnimationFrame(this._tooltipShowRaf);
    },

    render() {
        const {
            renderTitle,
            renderSearchInput,
            renderOptionList,
            renderCommonButtons,
            width,
            isVisible,
            appendToBody,
            Wrapper,
            offSetTop,
            extraComponents,
            className,
        } = this;

        const renderArray = [
            () => <div>{renderTitle()}</div>,
            () =>
                isVisible && [
                    <div>{renderSearchInput()}</div>,
                    <div>{renderOptionList()}</div>,
                ],
            () => <div>{renderCommonButtons()}</div>,
        ];

        if (extraComponents.length > 0) {
            extraComponents.forEach((v) => {
                const { dom = () => null, index = 0 } = v;
                renderArray.splice(index, 0, dom);
            });
        }

        return (
            <Wrapper
                offSetTop={offSetTop}
                width={width}
                isVisible={isVisible}
                appendToBody={appendToBody}
                class={className}
                id="commonDropboxWrapper"
                ref="dropboxWrapper"
            >
                {renderArray.map((dom) => dom())}
            </Wrapper>
        );
    },
};

const ItemElement = {
    functional: true,
    props: {
        source: {
            type: Object,
            default() {
                return {};
            },
        },
        selectedIdx: {
            type: Number,
            default: 0,
        },
        showSelected: {
            type: Boolean,
            default: true,
        },
        wrapTooltip: {
            type: Function,
            default: () => {},
        },
        lineClampOption: Object,
        renderOption: Function,
        tooltipGenerator: Function,
        blockedOptions: {
            type: Array,
            default() {
                return [];
            },
        },
        tooltipOnEllipsis: Boolean,
        onEllipsisOptionEnter: { type: Function, default: () => {} },
    },
    render(h, { props }) {
        const {
            source,
            selectedIdx,
            showSelected,
            wrapTooltip,
            lineClampOption,
            renderOption,
            tooltipGenerator,
            blockedOptions,
            tooltipOnEllipsis,
            onEllipsisOptionEnter,
        } = props;
        const { index, key, option } = source;

        // Check BOTH blocking patterns
        const isBlocked =
            blockedOptions.indexOf(option) > -1 || option?.disabled;

        const isSelectedOption = selectedIdx === index;

        const tooltip = tooltipGenerator(option);

        // Ellipsis-only path: skip the always-on wrapTooltip and instead let
        // the shared onEllipsisOptionEnter decide (via the single <a-tooltip>
        // anchored in renderOptionList) so the tooltip shows only when the
        // text is actually truncated.
        if (tooltipOnEllipsis) {
            return (
                <S.OptionItem
                    isSelectedOption={showSelected && isSelectedOption}
                    isBlocked={isBlocked}
                    key={key}
                    class="option-item"
                    data-index={index}
                    tooltipOnEllipsis={tooltipOnEllipsis}
                    onMouseenter={(e) =>
                        onEllipsisOptionEnter(key, tooltip, e)
                    }
                >
                    <p class="text">{renderOption(option)}</p>
                    {showSelected && isSelectedOption && (
                        <S.CheckItemWrapper>
                            <CheckIcon v-view-box />
                        </S.CheckItemWrapper>
                    )}
                </S.OptionItem>
            );
        }

        return wrapTooltip({
            visible: Boolean(tooltip),
            title: tooltip,
            content: (
                <S.OptionItem
                    isSelectedOption={showSelected && isSelectedOption}
                    isBlocked={isBlocked}
                    key={key}
                    class="option-item"
                    data-index={index}
                >
                    <p class="text" id={key} {...lineClampOption}>
                        {renderOption(option)}
                    </p>
                    {showSelected && isSelectedOption && (
                        <S.CheckItemWrapper>
                            <CheckIcon v-view-box />
                        </S.CheckItemWrapper>
                    )}
                </S.OptionItem>
            ),
        });
    },
};
